import React, { useState, useEffect, useMemo, useRef, useCallback, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { Gift, Sparkles, ArrowRight, ArrowLeft, Heart, Zap, Check, Star, X } from 'lucide-react';
import { analytics, referralTracker } from '../analytics';
import { detectUserLocation } from '../utils/location';
import { getGiftAltText } from '../utils/seoAltText';
import { useKeywordVariant } from '../hooks/useKeywordVariant';
import { getGiftResultsSchema } from '../utils/seoSchemas';
import { shouldHideOption, getInvalidAnswers } from '../utils/quizFilters';
import { getCurrencySymbol, convertCurrency } from '../utils/currencyUtils';
import { getBudgetLabel } from '../utils/budgetConverter';
import { getActiveSeasonalOccasions } from '../utils/seasonalOccasions';
import { trackQuizCompletion, trackDealClick, trackBuyClick, trackSave, trackSkip, saveQuizContext, getUserProfile, getOrCreateUID, isPersonalizedItem, getItemBoostCategory, personalizeResults, getBoostCategory } from '../utils/personalization';
import { getVariant } from '../abTest';
import { filterCatalogForQuiz, getProductLink } from '../data/productCatalog';
import { trackConversion } from '../utils/trackConversion';
import TinderSwipeResults from '../components/TinderSwipeResults';
import AppStoreBadges from '../components/AppStoreBadges';
import AppDownloadResultsCard from '../components/AppDownloadResultsCard';

// Lazy-load below-the-fold components to unblock LCP
const DealTeaser = lazy(() => import('../components/DealTeaser'));
const SEOContent = lazy(() => import('../components/SEOContent'));
const FAQ = lazy(() => import('../components/FAQ'));

const QUIZ_CONFIG = {
  questions: [
    { id: 'recipient', title: "Who's this gift for?", subtitle: "Help us understand the relationship", type: 'single', order: 1, isActive: true, options: [
      { value: 'boyfriend', label: 'Boyfriend / Husband', emoji: '🧔❤️' }, { value: 'girlfriend', label: 'Girlfriend / Wife', emoji: '👩❤️' },
      { value: 'mother', label: 'Mother', emoji: '👩' }, { value: 'father', label: 'Father', emoji: '👨' },
      { value: 'grandmother', label: 'Grandmother', emoji: '👵' }, { value: 'grandfather', label: 'Grandfather', emoji: '👴' },
      { value: 'friend', label: 'Friend', emoji: '👯' }, { value: 'child', label: 'Child', emoji: '👶' }, { value: 'colleague', label: 'Colleague', emoji: '💼' }, { value: 'sibling', label: 'Sibling', emoji: '👫' }
    ]},
    { id: 'age', title: "How old are they?", subtitle: "This helps us find age-appropriate gifts", type: 'single', order: 2, isActive: true, options: [
      { value: '0-5', label: '0-5 years', emoji: '👶' }, { value: '6-12', label: '6-12 years', emoji: '🧒' }, { value: '13-17', label: '13-17 years', emoji: '🧑' },
      { value: '18-24', label: '18-24 years', emoji: '🎓' }, { value: '25-34', label: '25-34 years', emoji: '💼' }, { value: '35-49', label: '35-49 years', emoji: '👨' },
      { value: '50-64', label: '50-64 years', emoji: '👴' }, { value: '65plus', label: '65+ years', emoji: '🧓' }
    ]},
    { id: 'occasion', title: "What's the occasion?", subtitle: "This helps us match the vibe", type: 'single', order: 3, isActive: true, options: [
      { value: 'birthday', label: 'Birthday', emoji: '🎂' }, { value: 'anniversary', label: 'Anniversary', emoji: '💍' }, { value: 'holiday', label: 'Holiday', emoji: '🎄' },
      { value: 'thankyou', label: 'Thank You', emoji: '🙏' }, { value: 'graduation', label: 'Graduation', emoji: '🎓' }, { value: 'justbecause', label: 'Just Because', emoji: '💝' }
    ]},
    { id: 'budget', title: "What's your budget?", subtitle: "We'll find great gifts in your range", type: 'single', order: 4, isActive: true, allowCustomInput: true, customInputType: 'budgetRange', customInputLabel: "Custom budget range", options: [
      { value: 'under25', emoji: '💵' }, { value: '25-50', emoji: '💵💵' }, { value: '50-100', emoji: '💰' },
      { value: '100-200', emoji: '💰💰' }, { value: '200plus', emoji: '💎' },
      { value: 'custom', label: 'Custom range...', emoji: '✏️', isCustomTrigger: true }
    ]},
    { id: 'interests', title: "What are they into?", subtitle: "Select all that apply", type: 'multiple', order: 5, isActive: true, allowCustomInput: true, customInputLabel: "Tell us more", customInputPlaceholder: "E.g., 'loves vintage records'...", options: [
      { value: 'tech', label: 'Tech & Gadgets', emoji: '📱' }, { value: 'fashion', label: 'Fashion & Style', emoji: '👗' }, { value: 'sports', label: 'Sports & Fitness', emoji: '⚽' },
      { value: 'books', label: 'Books & Reading', emoji: '📚' }, { value: 'food', label: 'Food & Cooking', emoji: '🍳' }, { value: 'gaming', label: 'Gaming', emoji: '🎮' },
      { value: 'art', label: 'Art & Crafts', emoji: '🎨' }, { value: 'travel', label: 'Travel', emoji: '✈️' }, { value: 'music', label: 'Music', emoji: '🎵' },
      { value: 'other', label: 'Something else...', emoji: '✏️', isCustomTrigger: true }
    ]},
    { id: 'style', title: "What gift style?", subtitle: "Select all vibes that fit", type: 'multiple', order: 6, isActive: true, options: [
      { value: 'practical', label: 'Practical & Useful', emoji: '🔧' }, { value: 'fun', label: 'Fun & Unique', emoji: '🎉' },
      { value: 'luxury', label: 'Luxury & Premium', emoji: '✨' }, { value: 'personal', label: 'Thoughtful & Personal', emoji: '❤️' }, { value: 'experience', label: 'Experience/Activity', emoji: '🎪' }
    ]},
    { id: 'additional_context', title: "Anything else we should know?", subtitle: "Optional - but helps!", type: 'text', order: 7, isActive: true, isOptional: true, placeholder: "E.g., 'They just moved'...", maxLength: 200 }
  ]
};

// Helper: Generate session ID
const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Helper: Get or create session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('pickify_session_id');
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem('pickify_session_id', sessionId);
  }
  return sessionId;
};

const HomePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState('welcome');
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [forceReset, setForceReset] = useState(false);

  // Check localStorage on mount to restore correct page
  useEffect(() => {
    // If force reset is active, stay on welcome
    if (forceReset) {
      setCurrentPage('welcome');
      setForceReset(false);
      return;
    }

    const quizComplete = localStorage.getItem('pickify_quiz_complete');
    const quizProgress = localStorage.getItem('pickify_quiz_progress');

    if (quizComplete) {
      // If quiz is completed, show results
      console.log('📦 Found completed quiz, showing results');
      setCurrentPage('results');
    } else if (searchParams.get('start') === 'quiz') {
      // If coming from hub page CTA, skip welcome and go straight to quiz
      console.log('🚀 Direct quiz start from hub page');
      setSearchParams({}, { replace: true });
      analytics.quizStarted();
      setCurrentPage('quiz');
    } else if (quizProgress) {
      // If quiz is in progress, show quiz
      console.log('📝 Found quiz in progress, resuming');
      setCurrentPage('quiz');
    } else {
      console.log('👋 No saved data, showing welcome');
    }
    // Otherwise stay on welcome page (default)
  }, [forceReset]);

  const handleRestart = () => {
    console.log('🔄 Restarting quiz - clearing all data');
    localStorage.removeItem('pickify_quiz_progress');
    localStorage.removeItem('pickify_quiz_complete');
    setForceReset(true);
    setCurrentPage('welcome');
  };

  return (
    <div className="App">
      <Helmet>
        <title>AI Gift Finder & Shopping Assistant - Personalized Gift Ideas in 30 Seconds [Free] | Pickify</title>
        <meta name="description" content="AI gift finder & last-minute shopping assistant. Answer 5 quick questions and get 4 personalized gift ideas for men, women &amp; kids with buy links in 30 seconds. Free, no signup needed." />
        <link rel="canonical" href="https://pickifyai.com/" />
        <link rel="alternate" hreflang="en" href="https://pickifyai.com/" />
        <link rel="alternate" hreflang="x-default" href="https://pickifyai.com/" />
        <meta property="og:site_name" content="Pickify - AI Gift Finder" />
      </Helmet>
      {currentPage === 'welcome' && <WelcomePage onStart={() => setCurrentPage('quiz')} />}
      {currentPage === 'quiz' && (
        <QuizPage
          onComplete={() => setCurrentPage('results')}
          selectedCurrency={selectedCurrency}
          setSelectedCurrency={setSelectedCurrency}
          userLocation={userLocation}
          setUserLocation={setUserLocation}
        />
      )}
      {currentPage === 'results' && (
        <ResultsPage
          onRestart={handleRestart}
          selectedCurrency={selectedCurrency}
          setSelectedCurrency={setSelectedCurrency}
          userLocation={userLocation}
          setUserLocation={setUserLocation}
        />
      )}
    </div>
  );
};

// Map seasonal events to recipient-specific messaging
const SEASONAL_RECIPIENT_MAP = {
  mothersday: { recipient: 'mother', ctaLabel: 'Find a Gift for Mom' },
  israelimothersday: { recipient: 'mother', ctaLabel: 'Find a Gift for Mom' },
  egyptianmothersday: { recipient: 'mother', ctaLabel: 'Find a Gift for Mom' },
  fathersday: { recipient: 'father', ctaLabel: 'Find a Gift for Dad' },
  israelifathersday: { recipient: 'father', ctaLabel: 'Find a Gift for Dad' },
  valentines: { recipient: null, ctaLabel: 'Find a Romantic Gift' },
  graduation: { recipient: null, ctaLabel: 'Find a Graduation Gift' },
};

const WelcomePage = ({ onStart }) => {
  const { headline, subheadline, badge, cta } = useKeywordVariant();
  const [seasonalBanner, setSeasonalBanner] = useState(null);
  const [seasonalContext, setSeasonalContext] = useState(null);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [returnVisitor, setReturnVisitor] = useState(null);

  useEffect(() => {
    const handleScroll = () => setShowStickyBar(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Check for return visitor
    const profile = getUserProfile();
    if (profile.visitCount > 0 && profile.lastRecipient) {
      setReturnVisitor(profile);
    }

    // Detect location for seasonal context
    detectUserLocation().then(location => {
      const occasions = getActiveSeasonalOccasions(location.countryCode);
      if (occasions.length === 0) return;
      const event = occasions[0];
      const now = new Date();
      const eventDate = new Date(now.getFullYear(), event.endMonth - 1, event.endDay);
      if (eventDate < now) return;
      const daysLeft = Math.ceil((eventDate - now) / (1000 * 60 * 60 * 24));
      if (daysLeft > 0 && daysLeft <= 45) {
        setSeasonalBanner({ value: event.value, label: event.label, emoji: event.emoji, daysLeft });
        // Set seasonal context for dynamic headline/CTA
        const mapping = SEASONAL_RECIPIENT_MAP[event.value];
        if (mapping) {
          setSeasonalContext({ ...mapping, label: event.label, occasion: event.value, emoji: event.emoji });
        }
      }
    }).catch(() => {});
  }, []);

  return (
    <main className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50">
      <div className="flex items-start justify-center p-4 pt-8">
        <div className="max-w-md w-full">
          <div className="text-center mb-3">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl mb-2 shadow-lg">
              <Gift className="text-white" size={28} />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-1">
              {seasonalContext ? `${seasonalContext.label} Gift Finder` : headline}
            </h1>
            <p className="text-gray-600 text-sm">
              {returnVisitor
                ? `Welcome back! Answer 5 quick questions for new gift ideas`
                : 'Answer 5 quick questions, get 4 personalized gift ideas'}
            </p>
          </div>
          {seasonalBanner && (
            <div className="flex items-center justify-center gap-2 px-4 py-2.5 mb-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl">
              <span className="text-lg">{seasonalBanner.emoji}</span>
              <span className="text-sm font-semibold text-amber-800">
                {seasonalBanner.label} is in {seasonalBanner.daysLeft} day{seasonalBanner.daysLeft !== 1 ? 's' : ''} — find the perfect gift!
              </span>
            </div>
          )}
          {/* Return visitor — quick re-run with last quiz context */}
          {returnVisitor && returnVisitor.lastRecipient && !seasonalContext && (
            <button
              onClick={() => {
                localStorage.setItem('pickify_quiz_progress', JSON.stringify({
                  currentStep: 0,
                  answers: { recipient: returnVisitor.lastRecipient, occasion: returnVisitor.lastOccasion, budget: returnVisitor.budgetPreference },
                  customInputs: {},
                  budgetRange: { from: '', to: '' },
                }));
                analytics.quizStarted();
                trackQuizStep(0, 'quiz_started');
                onStart();
              }}
              className="w-full mb-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl text-left hover:border-blue-400 transition-all"
            >
              <p className="text-xs text-blue-600 font-semibold mb-0.5">Last time you searched for</p>
              <p className="text-sm font-bold text-gray-900">
                {returnVisitor.lastRecipient.charAt(0).toUpperCase() + returnVisitor.lastRecipient.slice(1)} · {returnVisitor.lastOccasion || 'gift'} · {returnVisitor.budgetPreference || 'any budget'}
              </p>
              <p className="text-xs text-blue-500 mt-1">Tap to find new ideas with same preferences →</p>
            </button>
          )}

          <div className="bg-white rounded-3xl shadow-2xl p-6 mb-6">
            <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500 mb-4">
              <span className="flex items-center gap-1"><Check size={14} className="text-green-500" /> Free</span>
              <span className="flex items-center gap-1"><Check size={14} className="text-green-500" /> 30 seconds</span>
              <span className="flex items-center gap-1"><Check size={14} className="text-green-500" /> No signup</span>
              <span className="flex items-center gap-1"><Check size={14} className="text-green-500" /> AI-powered</span>
            </div>
            <button
              onClick={() => {
                console.log('▶️ Start Quiz clicked');
                // Pre-fill quiz with seasonal context if available
                if (seasonalContext?.recipient) {
                  localStorage.setItem('pickify_quiz_progress', JSON.stringify({
                    currentStep: 0,
                    answers: { recipient: seasonalContext.recipient },
                    customInputs: {},
                    budgetRange: { from: '', to: '' },
                  }));
                }
                analytics.quizStarted();
                trackQuizStep(0, 'quiz_started');
                onStart();
              }}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold py-4 rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all flex items-center justify-center gap-2 animate-cta-pulse"
            >
              <span className="text-lg">{seasonalContext?.ctaLabel || cta}</span>
              <ArrowRight size={20} />
            </button>
            <div className="flex items-center justify-center gap-1.5 mt-3">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={12} className="text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <span className="text-xs text-gray-500">12,000+ gifts recommended</span>
            </div>
            <p className="text-center mt-3">
              <a href="/deals" className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                or browse today's deals →
              </a>
            </p>
          </div>
          {/* Secondary app download row — non-competing with primary Start Quiz CTA */}
          <div className="flex flex-col items-center mb-6">
            <p className="text-xs text-gray-400 mb-2">Prefer the app? Save your picks & get deal alerts</p>
            <AppStoreBadges placement="welcome_secondary" variant="auto" height={36} />
          </div>
          <div className="text-center mb-6">
            <p className="text-xs text-gray-400 mb-2">Popular right now</p>
            <div className="flex flex-wrap justify-center gap-2">
              {(() => {
                const picks = [
                  { label: 'Birthday', emoji: '🎂', occasion: 'birthday' },
                  { label: 'Anniversary', emoji: '💍', occasion: 'anniversary' },
                  { label: 'Just Because', emoji: '💝', occasion: 'justbecause' },
                ];
                // If seasonal event is active, add it as first option.
                // Use the event's own value — deriving a slug from the label
                // produced occasions the quiz doesn't know ("Raksha Bandhan" →
                // "rakshabandhan" instead of "rakhi") and silently mislabelled
                // the country-specific ones ("Mother's Day" → "mothersday" even
                // in Israel, where the value is "israelimothersday").
                if (seasonalBanner && !picks.some(p => p.label === seasonalBanner.label)) {
                  picks.unshift({ label: seasonalBanner.label, emoji: seasonalBanner.emoji, occasion: seasonalBanner.value });
                  picks.pop(); // Keep 3 total
                }
                return picks;
              })().map((q) => (
                <button
                  key={q.occasion}
                  onClick={() => {
                    localStorage.setItem('pickify_quiz_progress', JSON.stringify({
                      currentStep: 0,
                      answers: { occasion: q.occasion },
                      customInputs: {},
                      budgetRange: { from: '', to: '' },
                    }));
                    analytics.quizStarted();
                    trackQuizStep(0, 'quiz_started');
                    onStart();
                  }}
                  className="px-3 py-1.5 bg-white border border-gray-200 rounded-full text-xs text-gray-700 hover:border-orange-300 hover:bg-orange-50 transition-all"
                >
                  {q.emoji} {q.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Suspense fallback={null}>
        <DealTeaser />
        <SEOContent />
        <FAQ />
      </Suspense>

      {/* Sticky bottom CTA — appears when user scrolls past hero */}
      {showStickyBar && (
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-200 px-4 py-3 z-50 animate-slide-up">
          <div className="max-w-md mx-auto flex items-center gap-3">
            <button
              onClick={() => {
                analytics.quizStarted();
                trackQuizStep(0, 'quiz_started');
                onStart();
              }}
              className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2"
            >
              <Gift size={16} />
              {cta}
            </button>
            <a href="/deals" className="px-4 py-3 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
              Deals
            </a>
          </div>
        </div>
      )}
    </main>
  );
};

const STEP_NAMES = ['recipient', 'age', 'occasion', 'budget', 'interests', 'style', 'additional_context'];

const trackQuizStep = (step, stepName) => {
  const sessionId = sessionStorage.getItem('pickify_session_id') ||
    `s_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  sessionStorage.setItem('pickify_session_id', sessionId);
  const uid = getOrCreateUID();
  fetch('/api/track', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'quiz-step', sessionId, step, stepName, uid }),
  }).catch(() => {});
};

const QuizPage = ({ onComplete, selectedCurrency, setSelectedCurrency, userLocation, setUserLocation }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [customInputs, setCustomInputs] = useState({});
  const [budgetRange, setBudgetRange] = useState({ from: '', to: '' });
  const [showValidation, setShowValidation] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState({});
  const [showCurrencyMenu, setShowCurrencyMenu] = useState(false);
  const activeQuestions = useMemo(() => {
    const baseQuestions = QUIZ_CONFIG.questions.filter(q => q.isActive).sort((a, b) => a.order - b.order);
    const activeSeasonals = getActiveSeasonalOccasions(userLocation?.countryCode);
    if (activeSeasonals.length === 0) return baseQuestions;

    return baseQuestions.map(q => {
      if (q.id !== 'occasion') return q;
      const seasonalOptions = activeSeasonals.map(s => ({
        value: s.value,
        label: s.label,
        emoji: s.emoji,
        isSeasonal: true
      }));
      const seasonalValues = new Set(seasonalOptions.map(o => o.value));
      const filteredOriginal = q.options.filter(o => !seasonalValues.has(o.value));
      return { ...q, options: [...seasonalOptions, ...filteredOriginal] };
    });
  }, [userLocation?.countryCode]);
  const currentQuestion = activeQuestions[currentStep];
  const currencySymbol = getCurrencySymbol(selectedCurrency || 'USD');
  const totalSteps = activeQuestions.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Restore quiz progress from localStorage on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    const savedProgress = localStorage.getItem('pickify_quiz_progress');
    if (savedProgress) {
      try {
        const progress = JSON.parse(savedProgress);
        setCurrentStep(progress.currentStep || 0);
        setAnswers(progress.answers || {});
        setCustomInputs(progress.customInputs || {});
        setBudgetRange(progress.budgetRange || { from: '', to: '' });
        console.log('✅ Restored quiz progress from localStorage');
      } catch (error) {
        console.error('Failed to restore quiz progress:', error);
      }
    } else {
      // No saved progress — check if return visitor has budget preference
      const profile = getUserProfile();
      if (profile.budgetPreference) {
        setAnswers(prev => ({ ...prev, budget: profile.budgetPreference }));
        console.log(`💡 Pre-filled budget from profile: ${profile.budgetPreference}`);
      }
    }
  }, []);

  // Track quiz abandonment when user leaves
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Only track if quiz is in progress (not completed)
      const quizComplete = localStorage.getItem('pickify_quiz_complete');
      if (!quizComplete && currentStep > 0) {
        analytics.quizAbandoned(currentStep + 1, activeQuestions[currentStep]?.id);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        const quizComplete = localStorage.getItem('pickify_quiz_complete');
        if (!quizComplete && currentStep > 0) {
          analytics.quizAbandoned(currentStep + 1, activeQuestions[currentStep]?.id);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentStep, activeQuestions]);

  // Save quiz progress to localStorage whenever it changes
  useEffect(() => {
    const progress = {
      currentStep,
      answers,
      customInputs,
      budgetRange,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('pickify_quiz_progress', JSON.stringify(progress));
  }, [currentStep, answers, customInputs, budgetRange]);

  // Scroll to top when question changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  useEffect(() => {
    if (!userLocation) {
      detectUserLocation().then(location => {
        console.log('📍 User location detected in quiz:', location.country);
        setUserLocation(location);
        if (!selectedCurrency) {
          setSelectedCurrency(location.currency);
        }
      }).catch(() => {
        if (!selectedCurrency) {
          setSelectedCurrency('USD');
        }
      });
    }
  }, []);

  const handleAnswer = (questionId, value) => {
    let newAnswer;
    if (currentQuestion.type === 'multiple') {
      const current = answers[questionId] || [];
      const option = currentQuestion.options.find(opt => opt.value === value);
      if (option?.isCustomTrigger) setShowCustomInput({ ...showCustomInput, [questionId]: !showCustomInput[questionId] });
      newAnswer = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      setAnswers({ ...answers, [questionId]: newAnswer });
    } else if (currentQuestion.type === 'text') {
      newAnswer = value;
      setAnswers({ ...answers, [questionId]: value });
    } else {
      newAnswer = value;
      const newAnswers = { ...answers, [questionId]: value };

      // Check if any future answers are now invalid due to this change
      const invalidAnswerIds = getInvalidAnswers(newAnswers);
      invalidAnswerIds.forEach(invalidId => {
        if (invalidId !== questionId) {
          delete newAnswers[invalidId];
          console.log(`🧹 Cleared invalid answer for ${invalidId}`);
        }
      });

      setAnswers(newAnswers);

      // Auto-advance for single-select (skip if custom trigger needs further input)
      const selectedOption = currentQuestion.options.find(opt => opt.value === value);
      if (!selectedOption?.isCustomTrigger) {
        trackQuizStep(currentStep + 1, STEP_NAMES[currentStep] || `step_${currentStep}`);
        setCurrentStep(prev => Math.min(prev + 1, totalSteps - 1));
        setShowValidation(false);
      }
    }

    analytics.quizQuestionAnswered(currentStep + 1, questionId, newAnswer);
    setShowValidation(false);
  };

  const isAnswered = () => {
    if (currentQuestion.isOptional) return true;
    const answer = answers[currentQuestion.id];
    if (currentQuestion.type === 'text') return answer && answer.trim().length > 0;
    if (currentQuestion.type === 'multiple') return answer && answer.length > 0;
    return !!answer;
  };

  const handleNext = () => {
    if (!isAnswered()) { setShowValidation(true); return; }
    if (currentStep < totalSteps - 1) {
      trackQuizStep(currentStep + 1, STEP_NAMES[currentStep] || `step_${currentStep}`);
      setCurrentStep(currentStep + 1);
      setShowValidation(false);
    } else {
      trackQuizStep(currentStep + 1, STEP_NAMES[currentStep] || `step_${currentStep}`);
      trackQuizStep(8, 'quiz_completed');
      const finalCustomInputs = { ...customInputs };
      if (budgetRange.from && budgetRange.to) {
        finalCustomInputs.budget = `${currencySymbol}${budgetRange.from} to ${currencySymbol}${budgetRange.to}`;
      }
      const completeData = { answers, customInputs: finalCustomInputs, timestamp: new Date().toISOString() };
      localStorage.setItem('pickify_quiz_complete', JSON.stringify(completeData));

      // Clear quiz progress since it's completed
      localStorage.removeItem('pickify_quiz_progress');
      console.log('✅ Cleared quiz progress (quiz completed)');

      // Track quiz completion for personalization (interests get +3 points each)
      if (answers.interests && Array.isArray(answers.interests)) {
        trackQuizCompletion(answers.interests);
      }
      // Save quiz context for return visit personalization
      saveQuizContext(answers);

      analytics.quizCompleted(answers);
      referralTracker.markConverted(); // Track referral conversion
      onComplete(completeData);
    }
  };

  const isSelected = (value) => {
    const answer = answers[currentQuestion.id];
    if (currentQuestion.type === 'multiple') return answer && answer.includes(value);
    return answer === value;
  };

  const currencyOptions = [
    { code: userLocation?.currency || 'USD', label: `${getCurrencySymbol(userLocation?.currency || 'USD')} ${userLocation?.currency || 'USD'} (Local)`, isPrimary: true },
    { code: 'USD', label: '$ USD', isPrimary: true },
    { code: 'EUR', label: '€ EUR', isPrimary: true },
    { code: 'GBP', label: '£ GBP' },
    { code: 'JPY', label: '¥ JPY' },
    { code: 'CAD', label: 'CA$ CAD' },
    { code: 'AUD', label: 'A$ AUD' }
  ].filter((option, index, self) => index === self.findIndex(o => o.code === option.code));

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 flex flex-col">
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">Question {currentStep + 1} of {totalSteps}</span>

            <div className="relative">
              <button
                onClick={() => setShowCurrencyMenu(!showCurrencyMenu)}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border-2 border-blue-200 rounded-lg hover:border-blue-400 transition-all text-sm"
                title="Change currency"
              >
                <span className="font-semibold">{getCurrencySymbol(selectedCurrency || 'USD')}</span>
                <span className="text-xs font-semibold">{selectedCurrency || 'USD'}</span>
                <svg className={`w-3 h-3 transition-transform ${showCurrencyMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showCurrencyMenu && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setShowCurrencyMenu(false)} />
                  <div className="absolute top-full right-0 mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-xl z-30 min-w-[160px]">
                    {currencyOptions.map((option, i) => (
                      <button
                        key={option.code}
                        onClick={() => {
                          if (selectedCurrency !== option.code) {
                            analytics.currencyChanged(selectedCurrency || 'USD', option.code);
                          }
                          setSelectedCurrency(option.code);
                          setShowCurrencyMenu(false);
                        }}
                        className={`w-full text-left px-4 py-2 hover:bg-blue-50 transition-colors text-sm ${i === 0 ? 'rounded-t-xl' : ''} ${i === currencyOptions.length - 1 ? 'rounded-b-xl' : ''} ${option.isPrimary ? 'font-semibold border-b border-gray-100' : ''} ${selectedCurrency === option.code ? 'bg-blue-50 text-blue-600' : ''}`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div className="bg-blue-500 h-full rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-4 py-8">
        <div className="max-w-2xl w-full">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">{currentQuestion.title}</h2>
            <p className="text-gray-600">{currentQuestion.subtitle}</p>
            {currentQuestion.isOptional && <span className="inline-block mt-2 px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">Optional</span>}
            {currentQuestion.type === 'multiple' && (
              <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-full text-xs text-orange-600 font-medium animate-pulse">
                <ArrowRight size={14} />
                <span>Tap <strong>Next</strong> when you're ready</span>
              </div>
            )}
            {currentQuestion.id === 'budget' && (
              <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-xl text-sm">
                <span className="text-blue-700">💡 Currency: <strong>{selectedCurrency || 'USD'}</strong></span>
                <button
                  onClick={() => setShowCurrencyMenu(true)}
                  className="text-blue-600 underline hover:text-blue-800 font-medium"
                >
                  Change
                </button>
              </div>
            )}
          </div>
          {currentQuestion.type === 'text' ? (
            <div className="max-w-xl mx-auto">
              <textarea value={answers[currentQuestion.id] || ''} onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)} placeholder={currentQuestion.placeholder} maxLength={currentQuestion.maxLength} rows={4} className="w-full p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none" />
            </div>
          ) : (
            <div className="grid gap-3">
              {currentQuestion.options
                .filter(opt => !shouldHideOption(currentQuestion.id, opt.value, answers))
                .map((opt) => {
                  let displayLabel = opt.label;

                  // Generate dynamic budget labels based on selected currency
                  if (currentQuestion.id === 'budget' && !opt.isCustomTrigger) {
                    displayLabel = getBudgetLabel(opt.value, selectedCurrency || 'USD', currencySymbol);
                  } else if (opt.label) {
                    displayLabel = opt.label;
                  }

                  return (
                    <button key={opt.value} onClick={() => handleAnswer(currentQuestion.id, opt.value)} className={`p-4 rounded-2xl border-2 text-left transition-all ${isSelected(opt.value) ? 'border-blue-500 bg-blue-50' : opt.isSeasonal ? 'border-orange-200 bg-orange-50/50' : 'border-gray-200 bg-white'}`}>
                      <div className="flex items-center gap-4">
                        <span className="text-3xl">{opt.emoji}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{displayLabel}</span>
                          {opt.isSeasonal && <span className="px-2 py-0.5 bg-orange-100 text-orange-600 text-xs font-bold rounded-full">Trending Now</span>}
                        </div>
                        {isSelected(opt.value) && <Check className="ml-auto text-blue-500" size={20} />}
                      </div>
                    </button>
                  );
                })}
            </div>
          )}
          {currentQuestion.allowCustomInput && (showCustomInput[currentQuestion.id] || (answers[currentQuestion.id] || []).includes('other') || (answers[currentQuestion.id] === 'custom')) && (
            <div className="mt-4">
              {currentQuestion.customInputType === 'budgetRange' ? (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                  <p className="text-sm font-semibold text-gray-700 mb-3">{currentQuestion.customInputLabel}</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-gray-600 mb-1 block">From ({currencySymbol})</label>
                      <input
                        type="number"
                        value={budgetRange.from}
                        onChange={(e) => setBudgetRange({ ...budgetRange, from: e.target.value })}
                        placeholder="Min"
                        min="0"
                        className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none text-center text-lg font-semibold"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 mb-1 block">To ({currencySymbol})</label>
                      <input
                        type="number"
                        value={budgetRange.to}
                        onChange={(e) => setBudgetRange({ ...budgetRange, to: e.target.value })}
                        placeholder="Max"
                        min="0"
                        className="w-full p-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none text-center text-lg font-semibold"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <textarea value={customInputs[currentQuestion.id] || ''} onChange={(e) => setCustomInputs({ ...customInputs, [currentQuestion.id]: e.target.value })} placeholder={currentQuestion.customInputPlaceholder} rows={3} maxLength={200} className="w-full p-4 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:outline-none" />
              )}
            </div>
          )}
          {/* Intermediate product suggestions — show after key steps */}
          {(() => {
            // Show products after recipient (step 0) or occasion (step 2) is answered
            const showAfterSteps = [0, 2]; // recipient, occasion
            if (!showAfterSteps.includes(currentStep)) return null;
            const currentAnswer = answers[currentQuestion?.id];
            if (!currentAnswer) return null;

            const products = filterCatalogForQuiz({
              recipient: answers.recipient || null,
              occasion: answers.occasion || null,
              interests: [],
              budget: answers.budget || null,
              limit: 3,
            }).filter(p => p.image && p.price > 0);

            if (products.length === 0) return null;

            return (
              <div className="mt-4 mb-2 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100">
                <p className="text-xs font-semibold text-blue-700 mb-3">
                  {currentStep === 0 ? '💡 Popular gifts right now' : '🎯 Top picks for this occasion'}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  {products.map(p => (
                    <a
                      key={p.id}
                      href={getProductLink(p)}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      onClick={() => {
                        trackConversion({ giftTitle: p.name, giftPrice: p.salePrice || p.price, giftPosition: 0, clickSource: 'quiz_intermediate' });
                        if (p.category) trackBuyClick(p.category);
                      }}
                      className="bg-white rounded-xl p-2 border border-gray-100 hover:border-blue-300 hover:shadow-md transition-all text-center group"
                    >
                      <img src={p.image} alt={p.name} className="w-full aspect-square object-contain rounded-lg mb-1 group-hover:scale-105 transition-transform" loading="lazy" />
                      <p className="text-[10px] text-gray-700 line-clamp-2 font-medium">{p.name}</p>
                      <p className="text-xs font-bold text-orange-600 mt-0.5">${(p.salePrice || p.price).toFixed(0)}</p>
                    </a>
                  ))}
                </div>
                <p className="text-[10px] text-blue-500 text-center mt-2">Continue the quiz for AI-personalized picks →</p>
              </div>
            );
          })()}

          {showValidation && <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-4 mt-4"><p className="text-orange-700 text-sm text-center">Please answer to continue</p></div>}

          <div className="flex gap-3 mt-6">
            {currentStep > 0 && <button onClick={() => setCurrentStep(currentStep - 1)} className="flex-1 bg-white border-2 border-gray-300 text-gray-700 font-semibold py-4 rounded-2xl"><ArrowLeft size={20} className="inline mr-2" />Back</button>}
            {currentQuestion.isOptional ? (
              <>
                <button
                  onClick={handleNext}
                  className="flex-1 bg-white border-2 border-gray-300 text-gray-600 font-semibold py-4 rounded-2xl hover:border-gray-400 transition-all"
                >
                  Skip →
                </button>
                <button onClick={handleNext} className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold py-4 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                  <span>✨ See Results</span>
                  <ArrowRight size={20} className="inline ml-2" />
                </button>
              </>
            ) : (
              <button onClick={handleNext} className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold py-4 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                <span>{currentStep === totalSteps - 1 ? '✨ See Results' : 'Next'}</span>
                <ArrowRight size={20} className="inline ml-2" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ResultsPage = ({ onRestart, selectedCurrency, setSelectedCurrency, userLocation: parentUserLocation, setUserLocation: parentSetUserLocation }) => {
  const requestIdRef = React.useRef(0);
  const isFetchingRef = React.useRef(false);
  const [gifts, setGifts] = useState([]);
  const [extraGifts, setExtraGifts] = useState([]);
  const [originalGifts, setOriginalGifts] = useState([]);
  const [originalCurrency, setOriginalCurrency] = useState(null);
  const [quizCombination, setQuizCombination] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [cacheUsed, setCacheUsed] = useState(false);
  const [showCurrencyMenu, setShowCurrencyMenu] = useState(false);
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [streamingProgress, setStreamingProgress] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState(null);
  const [showQuizSummary, setShowQuizSummary] = useState(false);
  const [affiliateFilter, setAffiliateFilter] = useState(null);
  const [viewedGifts, setViewedGifts] = useState(new Set());
  const giftObserverRef = useRef(null);
  const [showRatingPrompt, setShowRatingPrompt] = useState(false);
  const [userRating, setUserRating] = useState(0);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackEmail, setFeedbackEmail] = useState('');
  const [feedbackSending, setFeedbackSending] = useState(false);

  // Quiz context for SEO alt-text on gift images and ItemList schema enrichment
  const quizContext = useMemo(() => {
    try {
      const p = JSON.parse(localStorage.getItem('pickify_quiz_progress') || '{}');
      const a = p.answers || {};
      return {
        recipient: a.recipient,
        occasion: a.occasion,
        age: a.age,
        budget: a.budget,
        interests: Array.isArray(a.interests) ? a.interests : [],
        style: Array.isArray(a.style) ? a.style : [],
      };
    } catch { return {}; }
  }, []);

  // A/B test: grid vs swipe results view
  const [abVariant] = useState(() => {
    const variant = getVariant('results_view');
    analytics.abTestAssigned('results_view', variant);
    return variant;
  });

  // Track gift visibility using IntersectionObserver
  const giftCardRef = useCallback((node, gift, isExtraGift = false) => {
    if (!node || !gift) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const giftKey = `${gift.id}-${isExtraGift}`;
            setViewedGifts((prev) => {
              if (!prev.has(giftKey)) {
                analytics.giftViewed(gift.id - 1, gift.title, isExtraGift);
                return new Set([...prev, giftKey]);
              }
              return prev;
            });
            observer.unobserve(node);
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(node);
  }, []);

  // Show rating prompt after delay (only once per user)
  useEffect(() => {
    const hasRated = localStorage.getItem('pickify_has_rated');
    if (hasRated) return;

    // Only start timer once we have results
    if (isLoading || gifts.length === 0) return;

    console.log('⭐ Starting rating prompt timer (8s)...');
    const timer = setTimeout(() => {
      console.log('⭐ Showing rating prompt!');
      setShowRatingPrompt(true);
    }, 8000); // Show after 8 seconds

    return () => clearTimeout(timer);
  }, [isLoading, gifts.length]);

  const handleRatingSubmit = (rating) => {
    setUserRating(rating);
    localStorage.setItem('pickify_user_rating', rating.toString());
    analytics.userRated && analytics.userRated(rating);
    // Show feedback form for optional text/email
    setShowFeedbackForm(true);
  };

  const handleFeedbackSubmit = async (skipFeedback = false) => {
    setFeedbackSending(true);
    localStorage.setItem('pickify_has_rated', 'true');

    try {
      await fetch('/api/save-feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionStorage.getItem('pickify_session_id'),
          rating: userRating,
          feedback: skipFeedback ? null : feedbackText || null,
          email: skipFeedback ? null : feedbackEmail || null,
          country: parentUserLocation?.country || null
        })
      });
    } catch (error) {
      console.error('Failed to save feedback:', error);
    }

    setFeedbackSending(false);
    setRatingSubmitted(true);
    setTimeout(() => setShowRatingPrompt(false), 2000);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    detectUserLocation().then(location => {
      console.log('📍 User location detected:', location.country);
      parentSetUserLocation(location);
      if (!selectedCurrency) {
        setSelectedCurrency(location.currency);
      }
      generateGiftsWithCache(location, selectedCurrency || location.currency);
    }).catch(() => {
      if (!selectedCurrency) {
        setSelectedCurrency('USD');
      }
      generateGiftsWithCache(null, selectedCurrency || 'USD');
    });
  }, []);

  const saveConversion = async (giftTitle, giftPrice, giftPosition, clickSource) => {
    try {
      const sessionId = getSessionId();
      const apiUrl = process.env.NODE_ENV === 'production'
        ? '/api/save-conversion'
        : '/api/save-conversion';

      await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, giftTitle, giftPrice, giftPosition, abVariant, clickSource })
      });
      console.log('💰 Conversion saved to database');
    } catch (error) {
      console.error('Failed to save conversion:', error);
    }
  };

  const saveToDatabase = async (quizData, gifts) => {
    try {
      const sessionId = getSessionId();
      const apiUrl = process.env.NODE_ENV === 'production'
        ? '/api/save-submission'
        : '/api/save-submission';

      await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, quizData, gifts })
      });
      console.log('💾 Saved to database');
    } catch (error) {
      console.error('Failed to save to database:', error);
    }
  };

  const trackAffiliateImpressions = async (gifts, quizCombination, countryCode) => {
    try {
      const sessionId = getSessionId();
      const apiUrl = process.env.NODE_ENV === 'production'
        ? '/api/track'
        : '/api/track';

      for (const gift of gifts) {
        await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId,
            quizCombination,
            countryCode,
            affiliate: gift.storeName,
            giftTitle: gift.title,
            giftPrice: gift.price,
            giftPosition: gift.position,
            clicked: false,
            abVariant
          })
        });
      }
      console.log('📊 Tracked affiliate impressions');
    } catch (error) {
      console.error('Failed to track impressions:', error);
    }
  };

  const trackAffiliateClick = async (gift, quizCombination, countryCode) => {
    try {
      const sessionId = getSessionId();
      const apiUrl = process.env.NODE_ENV === 'production'
        ? '/api/track'
        : '/api/track';

      await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          quizCombination,
          countryCode,
          affiliate: gift.storeName,
          giftTitle: gift.title,
          giftPrice: gift.price,
          giftPosition: gift.position,
          clicked: true,
          abVariant
        })
      });
      console.log(`🎯 Tracked click on ${gift.storeName}: ${gift.title}`);
    } catch (error) {
      console.error('Failed to track click:', error);
    }
  };

  // Reusable buy-click handler shared by grid and swipe views
  // Handles tracking + navigation (app deep link on mobile, new tab on desktop)
  const handleBuyClick = useCallback((g, e, clickSource = 'quiz_grid') => {
    if (e) e.preventDefault();

    const isExtraGift = g.id > 4;
    if (isExtraGift) {
      analytics.seeMoreGiftClicked(g.title, g.price, g.id, g.storeName);
    } else {
      analytics.buyNowClicked(g.title, g.price, g.id - 1, g.storeName);
    }
    saveConversion(g.title, g.price, g.id, clickSource);

    if (quizCombination) {
      trackAffiliateClick(g, quizCombination, parentUserLocation?.countryCode || 'US');
    }

    if (g.category || g.interest) {
      trackBuyClick(g.category || g.interest);
    }
    if (isPersonalizedItem(g)) {
      const boostCategory = getItemBoostCategory(g);
      analytics.personalizedClick(boostCategory, g._personalizationSource || 'behavior', g.title, g.id);
    }

    // Open the affiliate link
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    const url = g.affiliateLink;

    if (isAndroid) {
      // Android: intent:// URLs open the native app with automatic browser fallback
      let pkg = null;
      if (url.includes('amazon.com') || url.includes('amzn.to')) pkg = 'com.amazon.mShop.android.shopping';
      else if (url.includes('aliexpress.com')) pkg = 'com.alibaba.aliexpresshd';

      if (pkg) {
        const cleanUrl = url.replace(/^https?:\/\//, '');
        window.location.href = `intent://${cleanUrl}#Intent;scheme=https;package=${pkg};S.browser_fallback_url=${encodeURIComponent(url)};end`;
      } else {
        window.location.href = url;
      }
    } else if (isIOS) {
      // iOS: try custom URL scheme to open native app, fall back to web after timeout
      let appScheme = null;
      if (url.includes('amazon.com') || url.includes('amzn.to')) appScheme = 'amazon';
      else if (url.includes('aliexpress.com')) appScheme = 'aliexpress';

      if (appScheme) {
        window.location.href = url.replace(/^https?/, appScheme);
        setTimeout(() => {
          if (!document.hidden) window.location.href = url;
        }, 1500);
      } else {
        window.location.href = url;
      }
    } else {
      // Desktop: open in new tab
      window.open(url, '_blank');
    }
  }, [quizCombination, parentUserLocation]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setEmailStatus('error');
      setEmailMessage('Please enter a valid email address');
      return;
    }

    setEmailStatus('loading');
    setEmailMessage('');

    try {
      const sessionId = getSessionId();
      const apiUrl = process.env.NODE_ENV === 'production'
        ? '/api/subscribe-email'
        : '/api/subscribe-email';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          sessionId,
          source: 'results_page',
          countryCode: parentUserLocation?.countryCode || null
        })
      });

      const data = await response.json();

      if (data.success) {
        setEmailStatus('success');
        setEmailMessage(data.message || 'Success! You\'ll get gift reminders.');
        setEmail('');
        setEmailSubmitted(true);

        try {
          analytics.emailSubscribed('results_page');
        } catch (analyticsError) {
          console.log('Analytics tracking failed, but subscription succeeded');
        }
      } else {
        setEmailStatus('error');
        setEmailMessage(data.error || 'Something went wrong');
      }
    } catch (error) {
      console.error('Email subscription error:', error);
      setEmailStatus('error');
      setEmailMessage('Failed to subscribe. Please try again.');
    }
  };

  const handleShare = async (platform) => {
    // First, save results to backend and get shareable ID
    let shareableId = null;

    // Check if localhost - use localStorage demo mode
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    if (isLocalhost) {
      // Demo mode: Generate random ID and store in localStorage
      const demoId = Math.random().toString(36).substring(2, 8);
      const shareableData = {
        gifts: gifts.map(g => ({
          id: g.id,
          title: g.title,
          description: g.description,
          price: g.price,
          currency: g.currency,
          imageEmoji: g.imageEmoji,
          imageColor: g.imageColor,
          productImage: g.productImage || null,
          affiliateLink: g.affiliateLink,
          storeName: g.storeName,
          aiReason: g.aiReason,
          aiConfidence: g.aiConfidence,
          badges: g.badges,
          trending: g.trending,
          rating: g.rating,
          reviewCount: g.reviewCount,
          wasPrice: g.wasPrice
        })),
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(`shareable_${demoId}`, JSON.stringify(shareableData));
      shareableId = demoId;
      console.log('🎭 Demo mode: Saved shareable results with ID:', demoId);
    } else {
      // Production mode: Call backend API
      try {
        const apiUrl = '/api/shareable-results';
        console.log('🔗 Creating shareable link...');
        console.log('🎁 Gifts to share:', gifts.length);

        const requestBody = {
          gifts: gifts.map(g => ({
            id: g.id,
            title: g.title,
            description: g.description,
            price: g.price,
            currency: g.currency,
            imageEmoji: g.imageEmoji,
            imageColor: g.imageColor,
            productImage: g.productImage || null,
            affiliateLink: g.affiliateLink,
            storeName: g.storeName,
            aiReason: g.aiReason,
            aiConfidence: g.aiConfidence,
            badges: g.badges,
            trending: g.trending,
            rating: g.rating,
            reviewCount: g.reviewCount,
            wasPrice: g.wasPrice
          })),
          quizData: JSON.parse(localStorage.getItem('pickify_quiz_complete') || '{}'),
          timestamp: new Date().toISOString()
        };

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });

        console.log('📡 API Response status:', response.status);
        const data = await response.json();
        console.log('📦 API Response data:', data);

        if (data.success && data.shareableId) {
          shareableId = data.shareableId;
          console.log('✅ Shareable ID created:', shareableId);
        } else {
          console.error('❌ API did not return shareableId:', data);
        }
      } catch (error) {
        console.error('❌ Failed to create shareable link:', error);
        // Fall back to homepage URL if API fails
      }
    }

    // Create shareable URL - use format=html for proper OG tags (redirects to /r/)
    const baseUrl = window.location.origin;
    const shareUrl = shareableId
      ? `${baseUrl}/api/shareable-results?id=${shareableId}&format=html`
      : `${baseUrl}/?ref=share`;

    // Get top gift for share text
    const topGift = gifts[0];
    const giftSummary = topGift ? `${topGift.title} (${getCurrencySymbol(topGift.currency)}${topGift.price})` : 'amazing gifts';

    const shareTitle = 'Pickify - AI Gift Finder';
    const shareText = `Check out these personalized gift recommendations I got from Pickify AI! 🎁 Top pick: ${giftSummary}`;
    const shareTextShort = `Check out these AI-recommended gifts from Pickify! 🎁`;

    // Try native Web Share API first (works great on mobile)
    if (platform === 'native' && navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareTextShort,
          url: shareUrl
        });
        analytics.resultsShared('native');
        setShowShareMenu(false);
        return;
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.log('Native share failed, falling back');
        }
        return;
      }
    }

    // Platform-specific sharing
    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank', 'width=550,height=420');
        break;
      case 'facebook':
        // Facebook will use Open Graph tags to generate preview
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareTextShort)}`, '_blank', 'width=550,height=420');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`, '_blank');
        break;
      case 'email':
        const emailSubject = 'Check out these gift ideas from Pickify AI!';
        const emailBody = `Hi!\n\n${shareText}\n\n${shareUrl}\n\nPickify uses AI to find personalized gift recommendations in just 30 seconds.`;
        window.location.href = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
        break;
      case 'copy':
        const copyText = `${shareTextShort}\n${shareUrl}`;
        navigator.clipboard.writeText(copyText).then(() => {
          setSavedMessage('✓ Link copied to clipboard!');
          setTimeout(() => setSavedMessage(''), 3000);
        });
        break;
      default:
        break;
    }
    setShowShareMenu(false);
    analytics.resultsShared(platform);
  };

  const handleSaveForLater = () => {
    const resultsData = {
      gifts: gifts.map(g => ({
        title: g.title,
        price: g.price,
        currency: g.currency,
        storeName: g.storeName,
        affiliateLink: g.affiliateLink
      })),
      timestamp: new Date().toISOString()
    };

    localStorage.setItem('pickify_saved_results', JSON.stringify(resultsData));
    setSavedMessage('✓ Results saved! You can access them anytime from this browser.');
    setTimeout(() => setSavedMessage(''), 4000);
    analytics.resultsSaved();
  };

  const handlePrint = () => {
    window.print();
    analytics.resultsPrinted();
  };

  const generateGiftsWithCache = (location, currency = null) => {
    if (isFetchingRef.current) return; // Block duplicate calls
    isFetchingRef.current = true;

    const myRequestId = ++requestIdRef.current;
    const isStale = () => requestIdRef.current !== myRequestId;

    const quizDataStr = localStorage.getItem('pickify_quiz_complete');
    if (!quizDataStr) {
      setIsLoading(false);
      isFetchingRef.current = false;
      return;
    }

    const quizData = JSON.parse(quizDataStr);

    // Parse quiz answers for display
    const parsedAnswers = [];
    QUIZ_CONFIG.questions.forEach(q => {
      if (!q.isActive) return;
      const answer = quizData.answers?.[q.id];
      if (!answer || (Array.isArray(answer) && answer.length === 0)) return;

      if (q.type === 'text') {
        if (answer.trim()) parsedAnswers.push({ title: q.title, items: [{ label: answer.trim(), emoji: '💬' }] });
      } else if (q.type === 'multiple') {
        const items = answer.map(val => {
          const opt = q.options.find(o => o.value === val);
          return opt ? { label: opt.label, emoji: opt.emoji } : { label: val, emoji: '•' };
        });
        if (items.length) parsedAnswers.push({ title: q.title, items });
      } else {
        // single select
        if (q.id === 'budget' && answer === 'custom' && quizData.customInputs?.budget) {
          parsedAnswers.push({ title: q.title, items: [{ label: quizData.customInputs.budget, emoji: '✏️' }] });
        } else {
          const opt = q.options.find(o => o.value === answer);
          if (opt) {
            const label = q.id === 'budget' ? getBudgetLabel(answer, currency || 'USD', getCurrencySymbol(currency || 'USD')) : opt.label;
            parsedAnswers.push({ title: q.title, items: [{ label, emoji: opt.emoji }] });
          }
        }
      }
    });
    setQuizAnswers(parsedAnswers);

    if (location) {
      quizData.location = { ...location };
      if (currency) {
        quizData.location.currency = currency;
      }
    } else if (currency) {
      quizData.location = { currency: currency };
    }

    const cacheKey = [
      quizData.answers.recipient || 'any',
      quizData.answers.occasion || 'any',
      quizData.answers.budget || 'any',
      quizData.customInputs?.budget || 'none',
      (quizData.answers.interests || []).sort().join('-') || 'none',
      (quizData.answers.style || []).sort().join('-') || 'none',
      quizData.location?.countryCode || 'US',
      quizData.location?.currency || 'USD'
    ].join('_');

    console.log('🔑 Cache key:', cacheKey);

    const cachedResults = localStorage.getItem(`gift_cache_${cacheKey}`);

    if (cachedResults) {
      console.log('✅ Using cached results! (Saved $0.02)');
      analytics.cacheUsed();
      const cachedGifts = JSON.parse(cachedResults);

      // Task 3: Apply personalization to cached results too
      const boostInfo = getBoostCategory();
      let finalGifts = cachedGifts;
      if (boostInfo) {
        const { items: personalizedGifts, boostedItems } = personalizeResults(cachedGifts, 2);
        finalGifts = personalizedGifts;
        boostedItems.forEach(item => {
          item._personalizationSource = boostInfo.source;
        });
        if (boostedItems.length > 0) {
          analytics.personalizedView(boostInfo.category, boostInfo.source, boostedItems.length);
        }
      }

      setGifts(finalGifts);
      setOriginalGifts(finalGifts);
      setOriginalCurrency(finalGifts[0]?.originalCurrency || finalGifts[0]?.currency || 'USD');
      setCacheUsed(true);
      setIsLoading(false);

      analytics.resultsViewed(finalGifts.length);
      saveToDatabase(quizData, finalGifts);
      isFetchingRef.current = false;
      return;
    }

    console.log('🔍 No cache, calling API...');

    // Demo mode disabled - always use real API
    // To re-enable demo mode, uncomment the localhost check below
    // const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const isLocalhost = false; // Force real API calls

    if (isLocalhost) {
      console.log('🎭 Demo mode: Generating mock gifts for local development');

      // Simulate API delay
      setTimeout(() => {
        const mockGifts = [
          {
            title: "Personalized Photo Album",
            description: "Beautiful leather-bound album with custom engraving",
            price: 45,
            currency: quizData.location?.currency || 'USD',
            emoji: "📷",
            affiliateLink: "https://amazon.com",
            affiliate: "Amazon",
            reason: "Perfect for preserving memories based on your personalized preferences",
            position: 1
          },
          {
            title: "Wireless Bluetooth Speaker",
            description: "Premium sound quality, 20-hour battery life, waterproof",
            price: 79,
            currency: quizData.location?.currency || 'USD',
            emoji: "🔊",
            affiliateLink: "https://amazon.com",
            affiliate: "Amazon",
            reason: "Great for music lovers and matches your tech interest",
            position: 2
          },
          {
            title: "Gourmet Chocolate Gift Box",
            description: "Hand-crafted artisan chocolates from around the world",
            price: 35,
            currency: quizData.location?.currency || 'USD',
            emoji: "🍫",
            affiliateLink: "https://etsy.com",
            affiliate: "Etsy",
            reason: "Sweet and thoughtful, perfect for the occasion you selected",
            position: 3
          },
          {
            title: "Cozy Cashmere Scarf",
            description: "Soft, luxurious cashmere in multiple colors",
            price: 68,
            currency: quizData.location?.currency || 'USD',
            emoji: "🧣",
            affiliateLink: "https://target.com",
            affiliate: "Target",
            reason: "Practical yet luxurious gift that fits your style preferences",
            position: 4
          }
        ];

        const colors = ['from-blue-400 to-purple-500', 'from-pink-400 to-red-500', 'from-green-400 to-teal-500', 'from-orange-400 to-yellow-500', 'from-indigo-400 to-blue-500', 'from-rose-400 to-pink-500', 'from-emerald-400 to-green-500', 'from-amber-400 to-orange-500', 'from-violet-400 to-purple-500', 'from-cyan-400 to-teal-500'];

        const newGifts = mockGifts.map((g, i) => {
          const confidenceScore = Math.max(85, 95 - (i * 3));

          return {
            id: i + 1,
            title: g.title,
            description: g.description,
            price: g.price,
            currency: g.currency,
            imageColor: colors[i % colors.length],
            imageEmoji: g.emoji,
            affiliateLink: g.affiliateLink,
            storeName: g.affiliate,
            badges: i === 0 ? ['AI Top Pick'] : ['AI Recommended'],
            aiReason: g.reason,
            aiConfidence: confidenceScore,
            trending: i < 2,
            position: g.position,
            originalPrice: g.price,
            originalCurrency: g.currency
          };
        });

        localStorage.setItem(`gift_cache_${cacheKey}`, JSON.stringify(newGifts));
        console.log('💾 Saved demo gifts to cache!');

        setGifts(newGifts);
        setOriginalGifts(newGifts);
        setOriginalCurrency(newGifts[0]?.currency || 'USD');
        setQuizCombination('demo-mode');
        setCacheUsed(false);
        setIsLoading(false);

        analytics.resultsViewed(newGifts.length);
        saveToDatabase(quizData, newGifts);
      }, 2000);

      return;
    }

    const apiUrl = '/api/generate-gifts';
    const colors = ['from-blue-400 to-purple-500', 'from-pink-400 to-red-500', 'from-green-400 to-teal-500', 'from-orange-400 to-yellow-500', 'from-indigo-400 to-blue-500', 'from-rose-400 to-pink-500', 'from-emerald-400 to-green-500', 'from-amber-400 to-orange-500', 'from-violet-400 to-purple-500', 'from-cyan-400 to-teal-500'];

    const processApiGifts = (aiGifts, startIndex = 0) => {
      return aiGifts.map((g, i) => {
        const idx = startIndex + i;
        const confidenceScore = Math.max(85, 95 - (idx * 3));
        return {
          id: idx + 1,
          title: g.title,
          description: g.description,
          price: g.price,
          currency: g.currency || 'USD',
          imageColor: colors[idx % colors.length],
          imageEmoji: g.emoji,
          productImage: g.catalogImage || g.aliexpressProductImage || null,
          affiliateLink: g.affiliateLink,
          storeName: g.affiliate,
          badges: idx === 0 ? ['AI Top Pick'] : ['AI Recommended'],
          aiReason: g.reason,
          aiConfidence: confidenceScore,
          trending: idx < 2,
          position: g.position || (idx + 1),
          rating: g.rating || null,
          reviewCount: g.reviewCount || null,
          originalPrice: g.price,
          originalCurrency: g.currency || 'USD',
          discountOriginalPrice: g.originalPrice || null
        };
      });
    };

    const handleError = (err) => {
      if (err.name === 'AbortError' || isStale()) return; // Ignore cancelled/stale requests
      console.error('❌ API Error:', err);

      // Track the error for analytics
      const errorType = err.message?.startsWith('RATE_LIMIT:') ? 'rate_limit' : 'api_error';
      analytics.apiError('generate-gifts', errorType, err.message || 'Unknown error');

      if (err.message && err.message.startsWith('RATE_LIMIT:')) {
        const resetTime = err.message.split(':')[1];
        setGifts([{
          id: 1, title: '⏰ Rate Limit Reached',
          description: `You've reached the maximum number of searches. Please try again in ${resetTime} minutes.`,
          price: 0, currency: 'USD', imageColor: 'from-orange-400 to-red-500', imageEmoji: '⏰',
          rating: 0, reviewCount: 0, affiliateLink: '#', badges: ['Rate Limit'],
          aiReason: 'To prevent abuse, we limit searches to 10 per hour. Your quota will reset soon!', trending: false
        }]);
      } else {
        setGifts([{
          id: 1, title: '⚠️ Unable to Generate Recommendations',
          description: err.message || 'An error occurred. Please try again in a few moments.',
          price: 0, currency: 'USD', imageColor: 'from-blue-400 to-purple-500', imageEmoji: '⚠️',
          rating: 0, reviewCount: 0, affiliateLink: '#', badges: ['Error'],
          aiReason: 'Please check your internet connection and try again.', trending: false
        }]);
      }
      setIsLoading(false);
      setIsLoadingMore(false);
    };

    // Clear previous results before streaming new ones
    setGifts([]);
    setOriginalGifts([]);
    setExtraGifts([]);
    setStreamingProgress(0);

    // Use streaming API for progressive results
    fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ quizData, stream: true })
    })
    .then(async (res) => {
      if (isStale()) return;
      console.log('📡 API Response status:', res.status);

      // Handle rate limit and errors (non-streaming responses)
      const contentType = res.headers.get('content-type') || '';
      if (!contentType.includes('text/event-stream')) {
        const data = await res.json();
        if (isStale()) return;
        if (res.status === 429) throw new Error(`RATE_LIMIT:${data.resetIn || 60}`);
        if (!res.ok || !data.success) throw new Error(data.error || 'Failed to generate gifts');

        // Non-streaming fallback: process all gifts at once
        const allGifts = processApiGifts(data.gifts);
        localStorage.setItem(`gift_cache_${cacheKey}`, JSON.stringify(allGifts));
        setGifts(allGifts);
        setOriginalGifts(allGifts);
        setOriginalCurrency(allGifts[0]?.currency || 'USD');
        if (data.extraGifts?.length) setExtraGifts(processApiGifts(data.extraGifts, 10));
        setQuizCombination(data.quizCombination);
        setCacheUsed(false);
        setIsLoading(false);
        analytics.resultsViewed(allGifts.length);
        saveToDatabase(quizData, allGifts);
        if (data.quizCombination) trackAffiliateImpressions(allGifts, data.quizCombination, location?.countryCode || 'US');
        return;
      }

      // Streaming SSE path: show gifts progressively
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      const streamedGifts = []; // Local array to avoid cross-stream accumulation

      while (true) {
        if (isStale()) { reader.cancel(); return; }
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop();

        for (const event of events) {
          if (isStale()) { reader.cancel(); return; }
          const dataLine = event.split('\n').find(l => l.startsWith('data: '));
          if (!dataLine) continue;

          let data;
          try { data = JSON.parse(dataLine.slice(6)); } catch (e) { continue; }

          if (data.type === 'error') {
            throw new Error(data.error || 'Server error');
          }

          if (data.type === 'gift') {
            console.log(`⚡ Gift #${data.index + 1} ready: ${data.gift.title}`);
            const processedGift = processApiGifts([data.gift], data.index)[0];
            streamedGifts.push(processedGift);
            setGifts([...streamedGifts]);
            setOriginalGifts([...streamedGifts]);
            setStreamingProgress(streamedGifts.length);
            if (data.index === 0) {
              setOriginalCurrency(processedGift.currency || 'USD');
            }
            setQuizCombination(data.quizCombination);
            setCacheUsed(false);
            setIsLoading(false);
            setIsLoadingMore(true);
          }

          if (data.type === 'gift-update') {
            // Progressive update: AliExpress product data arrived for a card
            const idx = data.index;
            if (streamedGifts[idx]) {
              const u = data.updates;
              streamedGifts[idx] = {
                ...streamedGifts[idx],
                title: u.title || streamedGifts[idx].title,
                affiliateLink: u.affiliateLink || streamedGifts[idx].affiliateLink,
                productImage: u.catalogImage || u.aliexpressProductImage || streamedGifts[idx].productImage,
                rating: u.rating || streamedGifts[idx].rating,
                reviewCount: u.reviewCount || streamedGifts[idx].reviewCount,
                price: u.price || streamedGifts[idx].price,
                discountOriginalPrice: u.originalPrice || null
              };
              setGifts([...streamedGifts]);
              setOriginalGifts([...streamedGifts]);
              console.log(`🔄 Card #${idx + 1} updated with AliExpress product data`);
            }
          }

          if (data.type === 'complete') {
            console.log('📦 All gifts finalized:', data.gifts.length);
            const allGifts = processApiGifts(data.gifts);

            // Task 3: Apply personalization to re-order gifts based on user profile/inbound signal
            const boostInfo = getBoostCategory();
            let finalGifts = allGifts;
            if (boostInfo) {
              const { items: personalizedGifts, boostedItems } = personalizeResults(allGifts, 2);
              finalGifts = personalizedGifts;
              // Mark boosted items with source info
              boostedItems.forEach(item => {
                item._personalizationSource = boostInfo.source;
              });
              // Fire personalized_view event if items were boosted
              if (boostedItems.length > 0) {
                analytics.personalizedView(boostInfo.category, boostInfo.source, boostedItems.length);
              }
            }

            localStorage.setItem(`gift_cache_${cacheKey}`, JSON.stringify(finalGifts));
            setGifts(finalGifts);
            setOriginalGifts(finalGifts);
            setOriginalCurrency(finalGifts[0]?.currency || 'USD');
            if (data.extraGifts?.length) setExtraGifts(processApiGifts(data.extraGifts, 10));
            setQuizCombination(data.quizCombination);
            setCacheUsed(false);
            setIsLoading(false);
            setIsLoadingMore(false);

            analytics.resultsViewed(finalGifts.length);
            saveToDatabase(quizData, finalGifts);
            if (data.quizCombination) trackAffiliateImpressions(finalGifts, data.quizCombination, location?.countryCode || 'US');
          }
        }
      }
    })
    .catch(handleError)
    .finally(() => { isFetchingRef.current = false; });
  };

  if (isLoading) return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 flex items-center justify-center">
      <div className="text-center">
        <Sparkles className="mx-auto mb-4 text-blue-500 animate-pulse" size={48} />
        <h2 className="text-3xl font-bold">Finding perfect gifts...</h2>
        <p className="text-gray-600 mt-2">Analyzing your preferences with AI</p>
        <p className="text-sm text-gray-400 mt-3">Usually takes under 30 seconds</p>
      </div>
    </div>
  );

  const currencyOptions = [
    { code: parentUserLocation?.currency || 'USD', label: `${getCurrencySymbol(parentUserLocation?.currency || 'USD')} ${parentUserLocation?.currency || 'USD'} (Local)`, isPrimary: true },
    { code: 'USD', label: '$ USD', isPrimary: true },
    { code: 'EUR', label: '€ EUR', isPrimary: true },
    { code: 'GBP', label: '£ GBP' },
    { code: 'JPY', label: '¥ JPY' },
    { code: 'CAD', label: 'CA$ CAD' },
    { code: 'AUD', label: 'A$ AUD' }
  ].filter((option, index, self) => index === self.findIndex(o => o.code === option.code));

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 p-4 py-8">
      {gifts.length > 0 && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(getGiftResultsSchema(gifts, quizContext))}</script>
        </Helmet>
      )}
      <div className="max-w-6xl mx-auto">
        {/* Header with Currency - Mobile Responsive */}
        <div className="mb-8">
          {/* Currency Selector - Top right on desktop, centered below title on mobile */}
          <div className="flex justify-end mb-2 sm:mb-0 sm:float-right sm:ml-4">
            <div className="relative">
              <button
                onClick={() => setShowCurrencyMenu(!showCurrencyMenu)}
                className="flex items-center gap-2 px-3 py-2 bg-white border-2 border-gray-200 rounded-xl hover:border-blue-400 transition-all shadow-sm"
                title="Change currency"
              >
                <span className="text-xl">{getCurrencySymbol(selectedCurrency || 'USD')}</span>
                <span className="text-sm font-semibold">{selectedCurrency || 'USD'}</span>
                <svg className={`w-4 h-4 transition-transform ${showCurrencyMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showCurrencyMenu && (
                <div className="absolute top-full right-0 mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-xl z-50 min-w-[160px]">
                  {currencyOptions.map((option, i) => (
                    <button
                      key={option.code}
                      onClick={() => {
                        if (selectedCurrency !== option.code) {
                          analytics.currencyChanged(selectedCurrency || 'USD', option.code);
                          setSelectedCurrency(option.code);
                          setShowCurrencyMenu(false);

                          if (originalGifts.length > 0) {
                            const convertedGifts = originalGifts.map(gift => ({
                              ...gift,
                              price: convertCurrency(
                                gift.originalPrice,
                                gift.originalCurrency,
                                option.code
                              ),
                              currency: option.code
                            }));
                            setGifts(convertedGifts);
                            console.log(`💱 Converted prices from ${originalCurrency} to ${option.code}`);
                          }
                        } else {
                          setShowCurrencyMenu(false);
                        }
                      }}
                      className={`w-full text-left px-4 py-2 hover:bg-blue-50 transition-colors ${i === 0 ? 'rounded-t-xl' : ''} ${i === currencyOptions.length - 1 ? 'rounded-b-xl' : ''} ${option.isPrimary ? 'font-semibold border-b border-gray-100' : ''} ${selectedCurrency === option.code ? 'bg-blue-50 text-blue-600' : ''}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Title and Info */}
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-2">
              {isLoadingMore ? 'Here Are Your First Picks! 🎁' : 'Your Perfect Gifts Are Ready! 🎁'}
            </h2>
            {parentUserLocation && (
              <p className="text-sm text-blue-600 mb-2">
                📍 Personalized for {parentUserLocation.country}
              </p>
            )}
            {!emailSubmitted && cacheUsed && (
              <p className="text-sm text-green-600 bg-green-50 inline-block px-4 py-2 rounded-full">
                ⚡ AI results generated instantly!
              </p>
            )}
          </div>
          <div className="clear-both"></div>
        </div>

        {/* Quiz Answers Summary */}
        {quizAnswers && quizAnswers.length > 0 && (
          <div className="max-w-4xl mx-auto mb-8">
            <button
              onClick={() => setShowQuizSummary(!showQuizSummary)}
              className="w-full bg-white border-2 border-gray-200 rounded-2xl px-5 py-4 hover:border-blue-300 transition-all shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-semibold text-gray-700 shrink-0">Your Preferences</span>
                  {!showQuizSummary && (
                    <div className="flex flex-wrap gap-1.5 overflow-hidden max-h-7">
                      {quizAnswers.flatMap(group => group.items).slice(0, 5).map((item, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-full whitespace-nowrap">
                          <span>{item.emoji}</span>
                          <span>{item.label}</span>
                        </span>
                      ))}
                      {quizAnswers.flatMap(group => group.items).length > 5 && (
                        <span className="text-xs text-gray-500 px-1">+{quizAnswers.flatMap(group => group.items).length - 5} more</span>
                      )}
                    </div>
                  )}
                </div>
                <svg className={`w-5 h-5 text-gray-500 shrink-0 ml-2 transition-transform ${showQuizSummary ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {showQuizSummary && (
                <div className="mt-4 pt-4 border-t border-gray-100 text-left space-y-3" onClick={e => e.stopPropagation()}>
                  {quizAnswers.map((group, gi) => (
                    <div key={gi} className="flex items-start gap-3">
                      <span className="text-xs text-gray-500 font-medium w-32 shrink-0 pt-1">{group.title.replace('?', '')}</span>
                      <div className="flex flex-wrap gap-1.5">
                        {group.items.map((item, ii) => (
                          <span key={ii} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">
                            <span>{item.emoji}</span>
                            <span>{item.label}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </button>
          </div>
        )}

        {/* Store Filter */}
        {gifts.length > 0 && (() => {
          const storeEmojis = { Amazon: '🛒', KSP: '🔌', AliExpress: '📦', Etsy: '🎨', Target: '🎯', Walmart: '💙' };
          const uniqueStores = [...new Set(gifts.map(g => g.storeName).filter(Boolean))];
          return (
            <div className="max-w-4xl mx-auto mb-6">
              {/* Filter Label Row */}
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm text-gray-500 font-medium">Filter by store:</span>
                {isLoadingMore && (
                  <div className="flex items-center gap-1.5 text-xs text-blue-500">
                    <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>loading...</span>
                  </div>
                )}
              </div>
              {/* Filter Buttons Row */}
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setAffiliateFilter(null)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${affiliateFilter === null ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  All ({gifts.length})
                </button>
                {uniqueStores.map(store => (
                  <button
                    key={store}
                    onClick={() => setAffiliateFilter(affiliateFilter === store ? null : store)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${affiliateFilter === store ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    <span>{storeEmojis[store] || '🏪'}</span>
                    <span>{store}</span>
                    <span className={`text-xs ${affiliateFilter === store ? 'text-blue-200' : 'text-gray-500'}`}>({gifts.filter(g => g.storeName === store).length})</span>
                  </button>
                ))}
                <div className="hidden sm:block w-px h-6 bg-gray-300 mx-1" />
                <div className="relative">
                  <button
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    className="px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:from-blue-600 hover:to-purple-600 shadow-md"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    <span>Share</span>
                  </button>
                  {showShareMenu && (
                    <>
                      <div className="fixed inset-0 z-20" onClick={() => setShowShareMenu(false)} />
                      <div className="absolute top-full left-0 mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-xl z-30 min-w-[220px] overflow-hidden">
                        {navigator.share && (
                          <button
                            onClick={() => handleShare('native')}
                            className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex items-center gap-3 bg-gradient-to-r from-blue-50 to-purple-50"
                          >
                            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                            </svg>
                            <span className="font-semibold text-blue-600">Share...</span>
                          </button>
                        )}
                        <button
                          onClick={() => handleShare('twitter')}
                          className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex items-center gap-3"
                        >
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                          </svg>
                          <span className="font-medium">Twitter / X</span>
                        </button>
                        <button
                          onClick={() => handleShare('facebook')}
                          className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex items-center gap-3 border-t border-gray-100"
                        >
                          <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                          </svg>
                          <span className="font-medium">Facebook</span>
                        </button>
                        <button
                          onClick={() => handleShare('whatsapp')}
                          className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex items-center gap-3 border-t border-gray-100"
                        >
                          <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                          <span className="font-medium">WhatsApp</span>
                        </button>
                        <button
                          onClick={() => handleShare('email')}
                          className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex items-center gap-3 border-t border-gray-100"
                        >
                          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <span className="font-medium">Email</span>
                        </button>
                        <button
                          onClick={() => handleShare('copy')}
                          className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex items-center gap-3 border-t border-gray-100"
                        >
                          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          <span className="font-medium">Copy Link</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* A/B Test: Swipe view vs Grid view */}
        {abVariant === 'swipe' ? (
          <TinderSwipeResults
            gifts={gifts.filter(g => !affiliateFilter || g.storeName === affiliateFilter)}
            onBuyClick={(g, e) => handleBuyClick(g, e, 'quiz_swipe')}
            currency={selectedCurrency}
            getCurrencySymbol={getCurrencySymbol}
          />
        ) : (
          <>
        {/* Gifts Grid - Show immediately */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
              {gifts.filter(g => !affiliateFilter || g.storeName === affiliateFilter).map(g => (
                <div key={g.id} ref={(node) => giftCardRef(node, g, g.id > 10)} className="bg-white rounded-3xl shadow-xl overflow-hidden flex flex-col">
                  <div className={`h-56 ${g.productImage ? 'bg-gradient-to-br from-gray-50 to-gray-100' : `bg-gradient-to-br ${g.imageColor}`} flex items-center justify-center relative overflow-hidden`}>
                    {g.productImage ? (
                      <img src={g.productImage} alt={getGiftAltText(g, quizContext)} className="h-full w-full object-contain p-6 mix-blend-multiply" loading="lazy" width="400" height="224" />
                    ) : isLoadingMore && g.storeName === 'AliExpress' ? (
                      <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 animate-pulse flex items-center justify-center">
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-16 h-16 rounded-xl bg-gray-200 animate-pulse" />
                          <div className="text-gray-400 text-xs font-medium">Finding product...</div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-8xl">{g.imageEmoji}</span>
                    )}
                    {/* AI Confidence Badge */}
                    {g.aiConfidence && (
                      <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm px-3 py-2 rounded-xl shadow-lg">
                        <div className="flex items-center gap-2">
                          <Sparkles size={14} className="text-purple-600" />
                          <div className="text-xs">
                            <div className="font-bold text-purple-600">{g.aiConfidence}% Match</div>
                            <div className="text-gray-500">AI Confidence</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    {/* Badge */}
                    {g.badges && g.badges.length > 0 && (
                      <div className="flex gap-2 mb-3">
                        {g.badges.map((badge, i) => (
                          <span key={i} className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 text-xs font-bold rounded-full">
                            <Sparkles size={12} />
                            {badge}
                          </span>
                        ))}
                      </div>
                    )}
                    <h3 className="text-xl font-bold mb-2">{g.title}</h3>
                    {!g.productImage && <p className="text-gray-600 text-sm mb-4">{g.description}</p>}
                    <div className="bg-purple-50 rounded-2xl p-4 mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles size={16} className="text-purple-600" />
                        <span className="text-sm font-bold text-purple-700">Why it's perfect:</span>
                      </div>
                      <ul className="space-y-1.5 text-sm text-gray-700">
                        {/* Convert reason to scannable bullet points */}
                        {(g.aiReason || '').split(/[.!,;]/).filter(s => s.trim().length > 10).slice(0, 4).map((point, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <Check size={14} className="text-purple-500 mt-0.5 flex-shrink-0" />
                            <span>{point.trim()}</span>
                          </li>
                        ))}
                        {/* Fallback if no split possible */}
                        {(g.aiReason || '').split(/[.!,;]/).filter(s => s.trim().length > 10).length === 0 && g.aiReason && (
                          <li className="flex items-start gap-2">
                            <Check size={14} className="text-purple-500 mt-0.5 flex-shrink-0" />
                            <span>{g.aiReason}</span>
                          </li>
                        )}
                      </ul>
                    </div>
                    <div className="space-y-3 mt-auto">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-baseline gap-2">
                            <span className="text-3xl font-bold text-gray-900">
                              {getCurrencySymbol(g.currency || 'USD')}{Math.round(g.price)}
                            </span>
                            {g.discountOriginalPrice && g.discountOriginalPrice > g.price && (
                              <span className="text-sm text-gray-500 line-through">
                                {getCurrencySymbol(g.currency || 'USD')}{Math.round(g.discountOriginalPrice)}
                              </span>
                            )}
                          </div>
                          {g.discountOriginalPrice && g.discountOriginalPrice > g.price && (
                            <div className="text-sm font-semibold text-green-700 mt-0.5">
                              Save {getCurrencySymbol(g.currency || 'USD')}{Math.round(g.discountOriginalPrice - g.price)}
                            </div>
                          )}
                        </div>
                        {g.trending && (
                          <div className="flex items-center gap-1 text-xs font-semibold text-orange-600 bg-orange-50 px-2 py-1 rounded-full">
                            <Zap size={12} />
                            <span>Trending</span>
                          </div>
                        )}
                      </div>

                      {/* Store Info */}
                      {g.storeName && (
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="w-5 h-5 bg-gray-100 rounded flex items-center justify-center">
                              {g.storeName === 'Amazon' && <span className="text-xs">🛒</span>}
                              {g.storeName === 'Etsy' && <span className="text-xs">🎨</span>}
                              {g.storeName === 'Target' && <span className="text-xs">🎯</span>}
                              {g.storeName === 'Walmart' && <span className="text-xs">💙</span>}
                              {g.storeName === 'KSP' && <span className="text-xs">🔌</span>}
                              {!['Amazon', 'Etsy', 'Target', 'Walmart', 'KSP'].includes(g.storeName) && <span className="text-xs">🏪</span>}
                            </div>
                            <span>Available on <strong>{g.storeName}</strong></span>
                          </div>
                          {/* KSP Local Pickup Badge - Israel Advantage */}
                          {g.storeName === 'KSP' && (
                            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-lg">
                              <span className="text-green-600 text-xs">📍</span>
                              <span className="text-xs font-bold text-green-700">Pick up today / איסוף עצמי היום</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Product Rating */}
                      <div className="flex items-center gap-2">
                        <div className="flex items-center">
                          {[1, 2, 3, 4, 5].map(star => (
                            <Star
                              key={star}
                              size={16}
                              className={g.rating && star <= Math.round(g.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
                            />
                          ))}
                        </div>
                        {g.rating ? (
                          <>
                            <span className="text-sm font-semibold text-gray-700">{g.rating}</span>
                            {g.reviewCount > 0 && (
                              <span className="text-xs text-gray-500">({g.reviewCount.toLocaleString()} reviews)</span>
                            )}
                            <span className="text-xs text-gray-500">on {g.storeName || 'store'}</span>
                          </>
                        ) : (
                          <span className="text-xs text-gray-500">No rating yet</span>
                        )}
                      </div>

                      {/* Enhanced CTA Button */}
                      <a href={g.affiliateLink} rel="noopener noreferrer" onClick={(e) => handleBuyClick(g, e)} className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold py-4 px-6 rounded-2xl hover:shadow-2xl hover:scale-105 transition-all flex items-center justify-center gap-2 group">
                        <span className="text-lg">View on {g.storeName || 'Store'}</span>
                        <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </a>

                      {/* Urgency Indicator for Top Picks */}
                      {g.id === 1 && (
                        <p className="text-xs text-center text-gray-500">
                          ⚡ Most popular choice for your profile
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Loading more gifts indicator */}
              {isLoadingMore && (
                <>
                  {[1, 2].map(i => (
                    <div key={`skeleton-${i}`} className="bg-white rounded-3xl shadow-xl overflow-hidden flex flex-col animate-pulse">
                      <div className="h-56 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                        <Sparkles size={40} className="text-gray-400 animate-spin" style={{ animationDuration: '3s' }} />
                      </div>
                      <div className="p-6 space-y-3">
                        <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                        <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-4 bg-gray-200 rounded w-full"></div>
                        <div className="h-20 bg-gray-100 rounded-2xl"></div>
                        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
                        <div className="h-12 bg-gray-200 rounded-2xl"></div>
                      </div>
                    </div>
                  ))}
                  <div className="col-span-full">
                    <div className="flex items-center justify-center gap-3 py-4 px-6 bg-blue-50 border-2 border-blue-200 rounded-2xl">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                        <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                        <span className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                      </div>
                      <span className="text-blue-700 font-semibold">{streamingProgress > 0 ? `${streamingProgress} of 10 gifts found` : 'Still searching for more gifts'}...</span>
                      <Sparkles size={18} className="text-blue-500 animate-pulse" />
                    </div>
                  </div>
                </>
              )}
        </div>
          </>
        )}

        {/* Find More Gifts Button */}
        {!isLoadingMore && extraGifts.length > 0 && (
          <div className="max-w-4xl mx-auto mb-8 text-center">
            <button
              onClick={() => {
                analytics.seeMoreClicked(gifts.length);
                const combined = [...gifts, ...extraGifts];
                setGifts(combined);
                setOriginalGifts(combined);
                setExtraGifts([]);
                analytics.resultsViewed(combined.length);
              }}
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold text-lg rounded-2xl hover:from-blue-600 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              <Sparkles size={22} />
              <span>Didn't find the perfect gift? See more ideas!</span>
              <ArrowRight size={22} />
            </button>
          </div>
        )}

        {/* Action Buttons - Share, Save, Print (hidden while loading more) */}
        {!isLoadingMore && <div className="max-w-4xl mx-auto mb-8">
          {savedMessage && (
            <div className="mb-4 p-4 bg-green-50 border-2 border-green-200 rounded-xl text-center text-green-700 font-medium">
              {savedMessage}
            </div>
          )}

          <div className="flex flex-wrap gap-3 justify-center">
            {/* Share Button */}
            <div className="relative">
              <button
                onClick={() => setShowShareMenu(!showShareMenu)}
                className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:border-blue-500 hover:text-blue-600 transition-all shadow-sm"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                <span>Share Results</span>
              </button>

              {showShareMenu && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setShowShareMenu(false)} />
                  <div className="absolute top-full left-0 mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-xl z-30 min-w-[220px] overflow-hidden">
                    {navigator.share && (
                      <button
                        onClick={() => handleShare('native')}
                        className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex items-center gap-3 bg-gradient-to-r from-blue-50 to-purple-50"
                      >
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                        </svg>
                        <span className="font-semibold text-blue-600">Share...</span>
                      </button>
                    )}
                    <button
                      onClick={() => handleShare('twitter')}
                      className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex items-center gap-3"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                      <span className="font-medium">Twitter / X</span>
                    </button>
                    <button
                      onClick={() => handleShare('facebook')}
                      className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex items-center gap-3 border-t border-gray-100"
                    >
                      <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                      <span className="font-medium">Facebook</span>
                    </button>
                    <button
                      onClick={() => handleShare('whatsapp')}
                      className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex items-center gap-3 border-t border-gray-100"
                    >
                      <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                      <span className="font-medium">WhatsApp</span>
                    </button>
                    <button
                      onClick={() => handleShare('email')}
                      className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex items-center gap-3 border-t border-gray-100"
                    >
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span className="font-medium">Email</span>
                    </button>
                    <button
                      onClick={() => handleShare('copy')}
                      className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors flex items-center gap-3 border-t border-gray-100"
                    >
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span className="font-medium">Copy Link</span>
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Save for Later Button */}
            <button
              onClick={handleSaveForLater}
              className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:border-purple-500 hover:text-purple-600 transition-all shadow-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              <span>Save for Later</span>
            </button>

            {/* Print/PDF Button */}
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:border-green-500 hover:text-green-600 transition-all shadow-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              <span>Print / PDF</span>
            </button>
          </div>
        </div>}

        {/* App Download Card — peak engagement moment, highest-converting placement */}
        <AppDownloadResultsCard />

        {/* Email Capture Section - After gifts, non-blocking */}
        <div className="max-w-2xl mx-auto my-12 bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl p-8 shadow-lg">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              💝 Never Miss a Special Occasion
            </h3>
            <p className="text-gray-600">
              Get gift reminders before birthdays, holidays, and anniversaries. Plus, exclusive Valentine's Day gift ideas!
            </p>
          </div>

          <form onSubmit={handleEmailSubmit} className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                disabled={emailStatus === 'loading' || emailStatus === 'success'}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-purple-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <button
                type="submit"
                disabled={emailStatus === 'loading' || emailStatus === 'success'}
                className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {emailStatus === 'loading' ? 'Subscribing...' : emailStatus === 'success' ? '✓ Subscribed!' : 'Get Reminders'}
              </button>
            </div>

            {emailMessage && (
              <div className={`mt-4 p-3 rounded-lg text-center ${
                emailStatus === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {emailMessage}
              </div>
            )}

            <p className="text-xs text-gray-500 text-center mt-3">
              No spam, unsubscribe anytime. We respect your privacy.
            </p>
          </form>
        </div>

        {/* Rating Prompt - Fixed at bottom */}
        {showRatingPrompt && !ratingSubmitted && (
          <div className="fixed bottom-4 left-4 right-4 z-50 flex justify-center animate-fade-in">
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-4 shadow-xl max-w-sm w-full relative">
              <button
                onClick={() => {
                  setShowRatingPrompt(false);
                  localStorage.setItem('pickify_has_rated', 'dismissed');
                }}
                className="absolute top-2 right-2 p-1 text-gray-500 hover:text-gray-700 transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
              {!showFeedbackForm ? (
                // Step 1: Star rating
                <div className="flex flex-col items-center gap-3">
                  <p className="text-sm font-medium text-gray-700">Did we help you find the perfect gift?</p>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-gray-300 w-8">Nope</span>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => handleRatingSubmit(star)}
                        className={`p-1 hover:scale-125 transition-all ${star >= 4 ? 'hover:drop-shadow-[0_0_8px_rgba(250,204,21,0.5)]' : ''}`}
                        aria-label={`Rate ${star} stars`}
                      >
                        <Star
                          size={star === 5 ? 32 : star === 4 ? 30 : 26}
                          className={`${star <= userRating ? 'fill-yellow-400 text-yellow-400' : star >= 4 ? 'text-yellow-200 hover:text-yellow-400' : 'text-gray-300 hover:text-yellow-400'} transition-colors`}
                        />
                      </button>
                    ))}
                    <span className="text-xs font-medium text-yellow-500 w-12">Yes!</span>
                  </div>
                  <button
                    onClick={() => {
                      setShowRatingPrompt(false);
                      localStorage.setItem('pickify_has_rated', 'dismissed');
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    Maybe later
                  </button>
                </div>
              ) : (
                // Step 2: Optional feedback form
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700">
                      {userRating >= 4 ? 'Awesome! Any suggestions?' : 'How can we improve?'}
                    </p>
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          size={14}
                          className={star <= userRating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
                        />
                      ))}
                    </div>
                  </div>
                  <textarea
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    placeholder="Tell us what you think... (optional)"
                    className="w-full p-2 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:border-blue-400"
                    rows={2}
                  />
                  <input
                    type="email"
                    value={feedbackEmail}
                    onChange={(e) => setFeedbackEmail(e.target.value)}
                    placeholder="Your email for follow-up (optional)"
                    className="w-full p-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleFeedbackSubmit(true)}
                      disabled={feedbackSending}
                      className="flex-1 py-2 text-sm text-gray-500 hover:text-gray-700"
                    >
                      Skip
                    </button>
                    <button
                      onClick={() => handleFeedbackSubmit(false)}
                      disabled={feedbackSending}
                      className="flex-1 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {feedbackSending ? 'Sending...' : 'Send Feedback'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Rating Thank You */}
        {showRatingPrompt && ratingSubmitted && (
          <div className="fixed bottom-4 left-4 right-4 z-50 flex justify-center animate-fade-in">
            <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-4 shadow-xl max-w-sm w-full text-center">
              <p className="text-green-700 font-medium">
                {userRating >= 4 ? '🎉 Thanks! Glad you loved it!' : '🙏 Thanks for your feedback!'}
              </p>
            </div>
          </div>
        )}

        <div className="text-center">
          <button onClick={() => {
            console.log('🔄 Start New Search clicked');
            analytics.restartClicked();
            onRestart();
          }} className="bg-white border-2 border-gray-300 text-gray-700 font-semibold py-3 px-8 rounded-2xl hover:border-blue-500 transition-all">
            🔄 Start New Search
          </button>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
